// soilColorUtil.ts

// NDVI Color Mapping (continuous, -1.0 to 1.0)
export const NDVI: Record<number, string> = {
  // "-1.0": "#FFFFFF", // White for -1.0 (no vegetation)
  0: "#6E120B",
  0.05: "#8F3A2E",
  0.1: "#C84F2B",
  0.15: "#BF281B",
  0.2: "#E93324",
  0.25: "#FF3E1F",
  0.3: "#FF7425",
  0.35: "#FF9B63",
  0.4: "#DE8D4B",
  0.45: "#E7BC72",
  0.5: "#F8C71D",
  0.6: "#E9EE33",
  0.65: "#CDE334",
  0.7: "#A7CE7F",
  0.75: "#9DC72D",
  0.8: "#6DAB25",
  0.85: "#4D9265",
  0.9: "#3F911F",
  0.95: "#006515",
  1: "#073010",
};

// Use this for continuous NDVI values
export function getColorForNDVIValue(value: number): string {
  // Clamp value to -1.0 ... 1.0
  value = Math.max(-1, Math.min(1, value));
  const thresholds = Object.keys(NDVI)
    .map(Number)
    .sort((a, b) => b - a);
  const matchingThreshold = thresholds.find((threshold) => value >= threshold);

  if (matchingThreshold === undefined) {
    return "#000000"; // Default color if no match
  }

  return NDVI[matchingThreshold];
}

// Default color schemes for different zone counts
export const DEFAULT_COLOR_SCHEMES: Record<number, string[]> = {
  1: ["#9DC72D"],
  2: ["#FF3E1F", "#9DC72D"],
  3: ["#FF3E1F", "#F8C71D", "#9DC72D"],
  4: ["#FF3E1F", "#F8C71D", "#CDE334", "#9DC72D"],
  5: ["#FF3E1F", "#FF9863", "#F8C71D", "#CDE334", "#9DC72D"],
  6: ["#FF3E1F", "#FF9863", "#E7BC72", "#F8C71D", "#CDE334", "#9DC72D"],
  7: [
    "#FF3E1F",
    "#FF9863",
    "#E7BC72",
    "#F8C71D",
    "#CDE334",
    "#9DC72D",
    "#4D9265",
  ],
  8: [
    "#BF281B",
    "#FF3E1F",
    "#FF9863",
    "#E7BC72",
    "#F8C71D",
    "#CDE334",
    "#9DC72D",
    "#4D9265",
  ],
  9: [
    "#BF281B",
    "#FF3E1F",
    "#FF9863",
    "#E7BC72",
    "#F8C71D",
    "#CDE334",
    "#9DC72D",
    "#4D9265",
    "#006515",
  ],
  10: [
    "#8F3A2E",
    "#BF281B",
    "#FF3E1F",
    "#FF9863",
    "#E7BC72",
    "#F8C71D",
    "#CDE334",
    "#9DC72D",
    "#4D9265",
    "#006515",
  ],
};

export const ACCESSIBILITY_COLOR_SCHEMES: Record<number, string[]> = {
  1: ["#9C790C"],
  2: ["#9C790C", "#BBA11E"],
  3: ["#9C790C", "#BBA11E", "#E9D85C"],
  4: ["#9C790C", "#BBA11E", "#E9D85C", "#B18041"],
  5: ["#9C790C", "#BBA11E", "#E9D85C", "#B18041", "#865719"],
  6: ["#9C790C", "#BBA11E", "#E9D85C", "#B18041", "#865719", "#E7BC72"],
  7: [
    "#9C790C",
    "#BBA11E",
    "#E9D85C",
    "#B18041",
    "#865719",
    "#E7BC72",
    "#DE8D4B",
  ],
  8: [
    "#9C790C",
    "#BBA11E",
    "#E9D85C",
    "#B18041",
    "#865719",
    "#E7BC72",
    "#DE8D4B",
    "#C84F2B",
  ],
  9: [
    "#9C790C",
    "#BBA11E",
    "#E9D85C",
    "#B18041",
    "#865719",
    "#E7BC72",
    "#DE8D4B",
    "#C84F2B",
    "#8F3A2E",
  ],
  10: [
    "#9C790C",
    "#BBA11E",
    "#E9D85C",
    "#B18041",
    "#865719",
    "#E7BC72",
    "#DE8D4B",
    "#C84F2B",
    "#8F3A2E",
    "#A34F6D",
  ],
  11: [
    "#9C790C",
    "#BBA11E",
    "#E9D85C",
    "#B18041",
    "#865719",
    "#E7BC72",
    "#DE8D4B",
    "#C84F2B",
    "#8F3A2E",
    "#A34F6D",
    "#C3708E",
  ],
  12: [
    "#9C790C",
    "#BBA11E",
    "#E9D85C",
    "#B18041",
    "#865719",
    "#E7BC72",
    "#DE8D4B",
    "#C84F2B",
    "#8F3A2E",
    "#A34F6D",
    "#C3708E",
    "#455588",
  ],
  13: [
    "#9C790C",
    "#BBA11E",
    "#E9D85C",
    "#B18041",
    "#865719",
    "#E7BC72",
    "#DE8D4B",
    "#C84F2B",
    "#8F3A2E",
    "#A34F6D",
    "#C3708E",
    "#455588",
    "#367FB2",
  ],
  14: [
    "#9C790C",
    "#BBA11E",
    "#E9D85C",
    "#B18041",
    "#865719",
    "#E7BC72",
    "#DE8D4B",
    "#C84F2B",
    "#8F3A2E",
    "#A34F6D",
    "#C3708E",
    "#455588",
    "#367FB2",
    "#4D9265",
  ],
  15: [
    "#9C790C",
    "#BBA11E",
    "#E9D85C",
    "#B18041",
    "#865719",
    "#E7BC72",
    "#DE8D4B",
    "#C84F2B",
    "#8F3A2E",
    "#A34F6D",
    "#C3708E",
    "#455588",
    "#367FB2",
    "#4D9265",
    "#A7CE7F",
  ],
};

// Zone-based color getter
export const getColorForZone = (
  zoneIndex: number,
  totalZones: number,
  palette: string = "DEFAULT"
): string => {
  let scheme: string[] = [];
  switch (palette.toUpperCase()) {
    case "ACCESSIBILITY":
      scheme =
        ACCESSIBILITY_COLOR_SCHEMES[totalZones] || COLOR_PALETTES.ACCESSIBILITY;
      break;
    case "NDVI":
      scheme = COLOR_PALETTES.NDVI;
      break;
    case "DEFAULT":
    default:
      scheme = DEFAULT_COLOR_SCHEMES[totalZones] || COLOR_PALETTES.DEFAULT;
      break;
  }
  return scheme[zoneIndex % scheme.length] || "#000000";
};

export const getDefaultColorPaletteValue = (
  index: number,
  totalZones: number
): string => {
  return getColorForZone(index, totalZones, "DEFAULT");
};

export const getAccessibilityPaletteValue = (
  index: number,
  totalZones: number
): string => {
  return getColorForZone(index, totalZones, "ACCESSIBILITY");
};

export const COLOR_PALETTES = {
  DEFAULT: [
    "#FF3E1F",
    "#F8C71D",
    "#CDE334",
    "#9DC72D",
    "#FF9863",
    "#E7BC72",
    "#4D9265",
    "#BF281B",
    "#006515",
    "#8F3A2E",
  ],
  ACCESSIBILITY: [
    "#9C790C",
    "#BBA11E",
    "#E9D85C",
    "#B18041",
    "#865719",
    "#E7BC72",
    "#DE8D4B",
    "#C84F2B",
    "#8F3A2E",
    "#A34F6D",
    "#C3708E",
    "#455588",
    "#367FB2",
    "#4D9265",
    "#A7CE7F",
  ],
  NDVI: [
    "#6E120B",
    "#8F3A2E",
    "#C84F2B",
    "#BF281B",
    "#E93324",
    "#FF3E1F",
    "#FF7425",
    "#FF9B63",
    "#DE8D4B",
    "#E7BC72",
    "#F8C71D",
    "#E9D85C",
    "#E9EE33",
    "#CDE334",
    "#A7CE7F",
    "#9DC72D",
    "#6DAB25",
    "#4D9265",
    "#3F911F",
    "#006515",
    "#073010",
  ],
};
